import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/constants/testIds";
import { NavLinks } from "@/components/navbar/NavLinks";
import { UserMenu } from "@/components/navbar/UserMenu";
import { MobileMenu } from "@/components/navbar/MobileMenu";

const PRINTIFY_URL = "https://bias-buster-store.printify.me/";

const navLinks = [
  { to: "/feed", label: "Voting Feed", testId: NAV.feed },
  { to: "/about", label: "Our Mission", testId: "nav-about-link" },
  { to: "/framework", label: "The Framework", testId: NAV.framework },
  { to: "/guidelines", label: "Guidelines", testId: NAV.guidelines },
  { to: "/guidelines?tab=resources", label: "Get Support", testId: NAV.getSupport },
  { to: "/bbap-book", label: "The BBAP Book", testId: NAV.greenbook },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-bbap-border bg-white/95 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Logo testId={NAV.logo} showText={false} size="h-16" />

          <NavLinks links={navLinks} />

          <div className="hidden xl:flex items-center gap-3">
            <a
              href={PRINTIFY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={NAV.merch}
              className="text-sm font-medium text-zinc-600 hover:text-[#1E3A6E] transition-colors"
            >
              Shop Merch
            </a>
            <Link to="/donate" data-testid={NAV.donate}>
              <Button className="bg-zinc-950 text-white font-medium uppercase tracking-wide text-xs rounded-none px-5 hover:bg-zinc-900 hover:shadow-[0px_4px_0px_0px_#4FD1C5] border border-transparent transition-all">
                Donate
              </Button>
            </Link>

            {user === false && (
              <>
                <Link to="/login" data-testid={NAV.login}>
                  <Button variant="ghost" className="text-zinc-700 hover:text-[#1E3A6E] hover:bg-zinc-100 rounded-none">Log In</Button>
                </Link>
                <Link to="/register" data-testid={NAV.register}>
                  <Button variant="outline" className="border-zinc-300 text-zinc-950 hover:border-zinc-950 rounded-none">Sign Up</Button>
                </Link>
              </>
            )}

            {user && <UserMenu user={user} onNavigate={navigate} onLogout={handleLogout} />}
          </div>

          <button
            className="xl:hidden text-zinc-950"
            onClick={() => setOpen(!open)}
            data-testid={NAV.mobileMenuToggle}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && <MobileMenu links={navLinks} user={user} onClose={() => setOpen(false)} onLogout={handleLogout} />}
    </header>
  );
}
